/**
 * "What are we having Tuesday?" — a searchable sheet of the meal library.
 * One tap plans the meal; the grocery list follows on its own.
 */
import { useMemo, useState } from 'react';
import type { Meal } from '../../shared/types';
import { EmptyState, MealPhoto, Modal, btnPrimary, btnSecondary, inputCls } from './ui';

export default function MealPicker({
  title,
  meals,
  onPick,
  onNewMeal,
  onClose,
}: {
  title: string;
  meals: Meal[];
  onPick: (meal: Meal) => void;
  onNewMeal: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return meals;
    return meals.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        (m.ingredients ?? []).some((i) => i.name.toLowerCase().includes(needle))
    );
  }, [meals, q]);

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className={`${btnSecondary} w-full`} onClick={onNewMeal}>
          <span aria-hidden="true">＋</span> New meal
        </button>
      }
    >
      {meals.length === 0 ? (
        <EmptyState
          title="No meals yet"
          body="Add the first thing your family actually eats — name it, list what it takes, and it'll show up here every week."
          action={
            <button type="button" className={btnPrimary} onClick={onNewMeal}>
              Add a meal
            </button>
          }
        />
      ) : (
        <div className="space-y-4">
          <div>
            <label className="sr-only" htmlFor="meal-search">
              Search meals
            </label>
            <input
              id="meal-search"
              type="search"
              className={inputCls}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search meals or ingredients"
            />
          </div>

          {matches.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-soft">
              Nothing matches “{q.trim()}”.
            </p>
          ) : (
            <ul className="space-y-2">
              {matches.map((meal) => (
                <li key={meal.id}>
                  <button
                    type="button"
                    onClick={() => onPick(meal)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface p-2 text-left transition-colors hover:bg-surface-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                  >
                    <MealPhoto
                      name={meal.name}
                      url={meal.photo_url}
                      className="size-12 shrink-0 rounded-lg"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink">{meal.name}</span>
                      <span className="block text-xs text-ink-faint">
                        {(meal.ingredients?.length ?? 0) === 0
                          ? 'No ingredients yet'
                          : `${meal.ingredients?.length} ingredient${
                              meal.ingredients?.length === 1 ? '' : 's'
                            }`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
